import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { Clock, AlertTriangle } from "lucide-react";
import { startOfDay } from "date-fns";

import { getCurrentEmployeeCheckinsDayWiseQueryOptions } from "@/config/queries/emp-query-options";
import { formatTimeToIST } from "@/utils/timezone";

export const LateBanner = () => {
  const key = Cookies.get("key") as string;

  // Stabilize today's date to prevent infinite re-renders
  const today = useMemo(() => startOfDay(new Date()), []);

  // Fetch today's checkins
  const { data: checkinsData, isLoading } = useQuery(
    getCurrentEmployeeCheckinsDayWiseQueryOptions(key, today)
  );

  // Get all IN checkins and sort by time to find the first one
  const checkins = checkinsData?.checkins || [];
  const inCheckins = checkins.filter((checkin) => checkin.mode === "IN");

  // Sort by createdAt (UTC) to get the first check-in
  const sortedInCheckins = [...inCheckins].sort(
    (a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const firstCheckin = sortedInCheckins[0];
  const isLate = firstCheckin?.isLate === true;

  // Don't show anything if loading or not late
  if (isLoading || !isLate || !firstCheckin) {
    return null;
  }

  // Format the check-in time - convert UTC to IST for display
  const checkinTime = formatTimeToIST(firstCheckin.createdAt);

  return (
    <div className="mb-4 p-4 rounded-lg border-2 border-orange-500 bg-orange-50 dark:bg-orange-950/30 flex items-start gap-3">
      <AlertTriangle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="text-orange-800 dark:text-orange-400 font-semibold flex items-center gap-2 text-lg">
          <Clock className="h-5 w-5" />
          Late Entry Today
        </div>
        <p className="text-orange-700 dark:text-orange-300 mt-1">
          You checked in at <span className="font-semibold">{checkinTime}</span>{" "}
          today, which is after the 9:15 AM threshold. Please ensure timely
          attendance to avoid penalties.
        </p>
      </div>
    </div>
  );
};
